import * as Sentry from '@sentry/vue';

import { resolveRuntimeContext } from './context.js';

const ALLOWED_ATTRIBUTE_MAP = {
  runtime_scene: true,
  os_family: true,
  device_class: true,
  route_name: true,
  api_name: true,
  method: true,
  result: true,
  failure_type: true,
  status: true,
  navigation_type: true
};

/**
 * 将驼峰运行上下文转为 Sentry Metrics attributes。
 * @returns {{runtime_scene:string,os_family:string,device_class:string}} 默认上下文属性。
 */
function getDefaultAttributes() {
  const context = resolveRuntimeContext();
  return {
    runtime_scene: context.runtimeScene,
    os_family: context.osFamily,
    device_class: context.deviceClass
  };
}

/**
 * 判断属性是否允许上报。
 * @param {string} key - 属性名。
 * @returns {boolean} 是否在白名单中。
 */
function isAllowedAttribute(key) {
  return Boolean(ALLOWED_ATTRIBUTE_MAP[key]);
}

/**
 * 过滤并序列化 Metrics 属性。
 * @param {Object} [attributes] - 原始属性。
 * @returns {Object} 可上报的低基数属性。
 */
function buildMetricAttributes(attributes = {}) {
  const merged = {
    ...getDefaultAttributes(),
    ...attributes
  };
  return Object.keys(merged).reduce((result, key) => {
    // 非白名单属性不能进入 Metrics，避免泄露高基数字段。
    if (!isAllowedAttribute(key)) {
      return result;
    }
    const value = merged[key];
    // 空属性不写入 Sentry，减少无意义维度。
    if (value === undefined || value === null || value === '') {
      return result;
    }
    result[key] = String(value).slice(0, 100);
    return result;
  }, {});
}

/**
 * 获取可用的 Sentry metrics API。
 * @returns {Object|null} Sentry metrics API。
 */
function getSentryMetrics() {
  // Sentry SDK 未初始化或未暴露 Metrics API 时静默跳过。
  if (!Sentry || !Sentry.metrics) {
    return null;
  }
  return Sentry.metrics;
}

/**
 * 安全记录 counter 指标。
 * @param {string} name - 指标名。
 * @param {Object} [attributes] - 低基数属性。
 * @returns {void}
 */
export function countMetric(name, attributes = {}) {
  try {
    const metrics = getSentryMetrics();
    // 当前 SDK 不支持 count 时静默返回，避免影响业务。
    if (!metrics || typeof metrics.count !== 'function') {
      return;
    }
    metrics.count(name, 1, {
      attributes: buildMetricAttributes(attributes),
      unit: 'none'
    });
  } catch (error) {}
}

/**
 * 安全记录 distribution 指标。
 * @param {string} name - 指标名。
 * @param {number} value - 指标值。
 * @param {string} unit - 指标单位。
 * @param {Object} [attributes] - 低基数属性。
 * @returns {void}
 */
export function recordDistribution(name, value, unit, attributes = {}) {
  try {
    const metrics = getSentryMetrics();
    // 当前 SDK 不支持 distribution 时静默返回，避免影响业务。
    if (!metrics || typeof metrics.distribution !== 'function') {
      return;
    }
    metrics.distribution(name, Number(value) || 0, {
      attributes: buildMetricAttributes(attributes),
      unit: unit || 'none'
    });
  } catch (error) {}
}

export { buildMetricAttributes };
